import { State } from '@meraki-internal/state';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { AsyncStorageProvider } from '../support/AsyncStorageProvider';
import { UserOnboardingState } from '../onboarding/UserOnboardingState';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { APP_FOREGROUNDED, APP_BACKGROUNDED } from '../AppContainer';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { BeeChatState } from './BeeChatState';
import { BeaSelector } from '../agency-bea/BeaSelector';
import { AgencyBeaState } from '../agency-bea/AgencyBeaState';

const SEARCH_PARAM_KEY = 'openChat';
export const HELPER_BEE_OPENED_STORAGE_KEY = 'helper-bee-opened';

export type IBeeChatTrackingFrom = 'Welcome' | 'Caregiver Tools' | 'Onboarding To Do' | 'Onboarding Revisited' | 'Deep Link' | 'Care Recipient Added' | 'Header Button' | 'Bea Comment' | 'Insight';

export class BeeChatViewModel extends State<Record<string, never>> {
    private _isOpen = false;
    private _isMinimized = false;
    private _restoringFromMinimize = false;
    private _pendingInitialMessage: string | undefined;

    // Location-specific ping tracking (home page vs header pages)
    private _homePingsEligible = true;
    private _homePingsActive = false;
    private _homeTimer: NodeJS.Timeout | null = null;

    private _headerPingsEligible = true;
    private _headerPingsActive = false;
    private _headerTimer: NodeJS.Timeout | null = null;

    private _isFirstForegroundEvent = true;

    static inject = () => [
        MixPanelEventEmitter,
        AsyncStorageProvider,
        UserOnboardingState,
        BufferedEventBus,
        HistoryViewModel,
        BeeChatState,
        BeaSelector,
        AgencyBeaState
    ];

    constructor(
        private mixpanel: MixPanelEventEmitter,
        private storageProvider: AsyncStorageProvider,
        private userOnboardingState: UserOnboardingState,
        private eventBus: BufferedEventBus,
        private history: HistoryViewModel,
        private beeChatState: BeeChatState,
        private beaSelector: BeaSelector,
        private agencyBea: AgencyBeaState
    ) {
        super({ });

        this.initSonarPings();
        this.initExtractionTriggers();

        this.history.listen(this.openIfSearchParam);
        this.openIfSearchParam();
    }

    private openIfSearchParam = () => {
        const params = this.history.getCurrentSearchParamMap();
        if (params[SEARCH_PARAM_KEY] !== undefined) {
            this.open({ trackingFrom: 'Deep Link' });
        }
    };

    private initSonarPings = () => {
        // Listen for app foreground to reset ping eligibility
        this.eventBus.on(APP_FOREGROUNDED, () => {
            // Skip the first buffered event (app is already initialized)
            if (this._isFirstForegroundEvent) {
                this._isFirstForegroundEvent = false;
                return;
            }
            this.resetPingEligibility();
        });
    };

    private initExtractionTriggers = () => {
        // Extraction fires on app-background while a chat is open or minimized.
        // close() handles the modal-close case directly. Minimize is intentionally NOT a
        // trigger — minimized chats are likely to be reopened, and re-extracting against the
        // same content would just spam the LLM.
        this.eventBus.on(APP_BACKGROUNDED, () => {
            if (this._isOpen || this._isMinimized) {
                this.beeChatState.extractMemoryIfActive();
            }
        });
    };

    private resetPingEligibility = () => {
        // Clear any existing timers
        if (this._homeTimer) {
            clearTimeout(this._homeTimer);
        }
        if (this._headerTimer) {
            clearTimeout(this._headerTimer);
        }

        // Reset both locations to eligible
        this._homePingsEligible = true;
        this._homePingsActive = false;
        this._headerPingsEligible = true;
        this._headerPingsActive = false;
        this.setState({});
    };

    private startPingsFor = (location: 'home' | 'header') => {
        if (location === 'home') {
            if (!this._homePingsEligible || this._homePingsActive) {
                return;
            }
            this._homePingsActive = true;
            this.setState({});
            this._homeTimer = setTimeout(() => {
                this._homePingsActive = false;
                this._homePingsEligible = false;
                this.setState({});
            }, 7500);
        } else {
            if (!this._headerPingsEligible || this._headerPingsActive) {
                return;
            }
            this._headerPingsActive = true;
            this.setState({});
            this._headerTimer = setTimeout(() => {
                this._headerPingsActive = false;
                this._headerPingsEligible = false;
                this.setState({});
            }, 7500);
        }
    };

    // Call this in useEffect to trigger pings for a location
    triggerPings = (location: 'home' | 'header') => {
        this.startPingsFor(location);
    };

    // Call this during render to check if pings should show
    showHomePings = () => this._homePingsActive;
    showHeaderPings = () => this._headerPingsActive;

    isOpen = () => this._isOpen;
    isMinimized = () => this._isMinimized;
    isRestoringFromMinimize = () => this._restoringFromMinimize;
    clearRestoringFlag = () => { this._restoringFromMinimize = false; };

    // Returns the message a caller asked to seed the conversation with, clearing it so it
    // sends at most once. BeeChatModal consumes this from its open effect AFTER settling the
    // conversation state (fresh reset or restore-from-minimize) — sending from open() itself
    // would race the modal's startNewConversation() and get wiped by the reset.
    consumePendingInitialMessage = (): string | undefined => {
        const message = this._pendingInitialMessage;
        this._pendingInitialMessage = undefined;
        return message;
    };

    private setStorageOpened = async () => {
        await this.storageProvider.getBooleanProvider(HELPER_BEE_OPENED_STORAGE_KEY).set(true);
    };

    open = (params: { trackingFrom: IBeeChatTrackingFrom; initialMessage?: string }) => {
        const { trackingFrom } = params;

        // Mark as opened for badge removal and onboarding completion, whichever Bea answers
        this.setStorageOpened();
        this.userOnboardingState.completeStep('opened-bea');

        // A member of an organization's staff gets agency Bea instead: over the caseload, or over
        // the client they have open.
        if (this.beaSelector.select() === 'agency') {
            // A deep link's param is spent here, as close() spends it for family Bea, or every
            // later history change would open the panel again; replaced, so it costs no back press.
            this.history.replaceWithoutSearchParam(SEARCH_PARAM_KEY);
            this._pendingInitialMessage = undefined;
            this.agencyBea.open({ surface: trackingFrom, initialMessage: params.initialMessage });
            return;
        }

        // Overwrite (or clear) on every open so a message from a previous open can't
        // leak into a later, unrelated conversation.
        this._pendingInitialMessage = params.initialMessage;

        // If minimized, restore instead of starting fresh
        if (this._isMinimized) {
            this.restore();
            return;
        }

        this._isOpen = true;
        this.setState({ });

        this.mixpanel.track('Native AI Chatbot Opened', () => ({ from: trackingFrom }));
    };

    minimize = () => {
        this._isOpen = false;
        this._isMinimized = true;
        this.setState({ });
        this.mixpanel.track('Native AI Chatbot Minimized', () => ({}));
    };

    restore = () => {
        this._isOpen = true;
        this._isMinimized = false;
        this._restoringFromMinimize = true;
        this.setState({ });
        this.mixpanel.track('Native AI Chatbot Restored', () => ({}));
    };

    close = () => {
        // Fire extraction before clearing UI state so BeeChatState.activeChat is still
        // readable. extractMemoryIfActive bails out if there are no user messages, so this
        // is cheap when the user opened-but-didn't-send.
        this.beeChatState.extractMemoryIfActive();
        this._isOpen = false;
        this._isMinimized = false;
        this.history.removeSearchParam(SEARCH_PARAM_KEY);
        this.setState({ });
    };
}
